import { useEffect, type JSX } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatAddress, formatPostalCode } from '@footsteps/shared';
import { useWozSelectAddressesModalViewModel } from './woz-select-addresses-modal.viewmodel';
import { styles } from '../woz-find-address-modal/woz-find-address-modal.styles';
import { multiStyles } from './woz-select-addresses-modal.styles';
import type { AddressData } from './woz-select-addresses-modal.types';

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (addresses: AddressData[]) => void;
};

export const WozSelectAddressesModal = ({ open, onClose, onConfirm }: Props): JSX.Element => {
  const [state, viewModel] = useWozSelectAddressesModalViewModel();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (open) viewModel.reset();
  }, [open, viewModel]);

  const hasErrors = Object.keys(state.errors).length > 0 || !!state.apiError;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      fullScreen={isMobile}
      slotProps={{ paper: { sx: styles.dialogPaper(isMobile) } }}
    >
      <DialogTitle sx={styles.dialogTitle}>
        Selecteer adres(sen)
        <IconButton aria-label='sluiten' onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={styles.dialogContent}>
        <Box sx={multiStyles.splitContainer(isMobile)}>
          {/* Left panel: search */}
          <Box sx={multiStyles.leftPanel}>
            <Typography variant='subtitle2' fontWeight={600}>
              Adres zoeken
            </Typography>

            <Box
              component='form'
              onSubmit={(e) => {
                e.preventDefault();
                viewModel.handleSearch();
              }}
              sx={styles.form(isMobile)}
            >
              <Box sx={styles.formFields}>
                <Box sx={styles.formField}>
                  <Typography variant='caption' className='form-label'>
                    Postcode
                  </Typography>
                  <TextField
                    placeholder='1234 AB'
                    value={formatPostalCode(state.postalCode)}
                    onChange={(e) => viewModel.setPostalCode(e.target.value)}
                    error={!!state.errors.postalCode}
                    fullWidth
                    slotProps={{ htmlInput: { maxLength: 7 } }}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant='caption' className='form-label'>
                    Huisnummer
                  </Typography>
                  <TextField
                    placeholder='42'
                    value={state.houseNumber}
                    onChange={(e) => viewModel.setHouseNumber(e.target.value)}
                    error={!!state.errors.houseNumber}
                    fullWidth
                    slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                  />
                </Box>
                <Box sx={styles.formField}>
                  <Typography variant='caption' className='form-label'>
                    Toevoeging
                  </Typography>
                  <TextField
                    placeholder='A'
                    value={state.houseNumberAddition}
                    onChange={(e) => viewModel.setHouseNumberAddition(e.target.value)}
                    fullWidth
                  />
                </Box>
              </Box>
              <Button
                type='submit'
                variant='contained'
                size='small'
                disabled={state.isLoading}
                startIcon={
                  state.isLoading ? <CircularProgress size={16} color='inherit' /> : <SearchIcon fontSize='small' />
                }
                sx={styles.searchButton(isMobile)}
                fullWidth={isMobile}
              >
                Zoeken
              </Button>
            </Box>

            {hasErrors && (
              <Alert severity='warning' sx={{ mt: 1 }}>
                {Object.values(state.errors).map((error, index) => (
                  <Typography key={index} variant='body2'>
                    {error}
                  </Typography>
                ))}
                {state.apiError && <Typography variant='body2'>{state.apiError}</Typography>}
              </Alert>
            )}

            {state.results.length > 0 && (
              <Box>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  Klik op een adres om toe te voegen:
                </Typography>
                <List dense sx={styles.resultList}>
                  {state.results.map((address) => {
                    const alreadySelected = viewModel.isSelected(address.addressPointId ?? address.id);
                    return (
                      <ListItemButton
                        key={address.id}
                        disabled={alreadySelected}
                        onClick={() => viewModel.addAddress(address)}
                      >
                        <ListItemText primary={address.displayName} slotProps={{ primary: { variant: 'body2' } }} />
                        {alreadySelected ? (
                          <Chip label='Toegevoegd' size='small' color='success' variant='outlined' />
                        ) : (
                          <AddIcon fontSize='small' color='primary' />
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            )}
          </Box>

          {/* Right panel: selected addresses */}
          <Box sx={multiStyles.rightPanel}>
            <Typography variant='subtitle2' fontWeight={600}>
              Geselecteerde adressen ({state.selectedAddresses.length})
            </Typography>

            {state.selectedAddresses.length === 0 ? (
              <Box sx={multiStyles.selectedList}>
                <Box sx={multiStyles.emptyState}>
                  <Typography variant='body2'>Nog geen adressen geselecteerd</Typography>
                </Box>
              </Box>
            ) : (
              <List dense sx={multiStyles.selectedList}>
                {state.selectedAddresses.map((address) => (
                  <ListItem
                    key={address.addressPointId}
                    secondaryAction={
                      <IconButton
                        edge='end'
                        aria-label='verwijderen'
                        size='small'
                        onClick={() => viewModel.removeAddress(address.addressPointId)}
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={formatAddress(address)} slotProps={{ primary: { variant: 'body2' } }} />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={styles.dialogActions}>
        <Button size='small' onClick={onClose} sx={styles.textButton}>
          Sluiten
        </Button>
        <Button
          size='small'
          variant='contained'
          onClick={() => viewModel.handleConfirm(onConfirm)}
          disabled={!state.canConfirm}
          sx={styles.confirmButton}
        >
          Bevestigen ({state.selectedAddresses.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};
